import React, { useState, useEffect } from "react";
import {
  Modal,
  Group,
  Button,
  Progress,
  Text,
  Paper,
  Box,
  Stack,
  Center,
} from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { useTemplate } from "../../../context/TemplateContext";
import { useSalon } from "../../../context/SalonContext";
import { buildBlankTemplateRequest } from "../utils/requestBuilder";
import TemplateSelectorWizard from "./TemplateSelectorWizard";
import WebsiteURLManagement from "./WebsiteURLManagement";
import { BusinessInfo } from "../../../types/context";

interface TemplateData {
  template_content?: string;
  template_type?: string;
  template_filename?: string;
}

interface WebsiteGeneratorWizardModalProps {
  onClose: () => void;
  onComplete: () => void;
  setTemplate: (template: TemplateData | null) => void;
  setIsTemplateLoading: (loading: boolean) => void;
  businessInfo?: BusinessInfo | null;
  loadedPreviewData?: any;
}

const WebsiteGeneratorWizardModal: React.FC<
  WebsiteGeneratorWizardModalProps
> = ({
  onClose,
  onComplete,
  setTemplate,
  setIsTemplateLoading,
  businessInfo,
  loadedPreviewData,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [templateSelected, setTemplateSelected] = useState("website");
  const [templateFetchError, setTemplateFetchError] = useState<string | null>(
    null,
  );
  // Logo upload moved to Settings → My Business → Branding, so the wizard is
  // now Template → Website address.
  const totalSteps = 2;

  const { generateManualPreview } = useTemplate();
  const salonCtx = useSalon() as any;
  // Use businessInfo from context to ensure we get the latest value after logo upload
  const salonBusinessInfo =
    (salonCtx?.businessInfo as BusinessInfo | null | undefined) ??
    (businessInfo as BusinessInfo | null | undefined);

  const handleNext = () => {
    if (currentStep === 1) {
      fetchSelectedTemplate();
    }

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    if (currentStep === 1) {
      fetchSelectedTemplate();
    }

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const fetchSelectedTemplate = async () => {
    try {
      setIsTemplateLoading(true);
      setTemplateFetchError(null);

      // Get the latest businessInfo from context to ensure we have the updated logo
      const currentBusinessInfo =
        (salonCtx?.businessInfo as BusinessInfo | null | undefined) ??
        salonBusinessInfo;

      // Build blank request with only template_type and company name
      const blankRequest = buildBlankTemplateRequest(
        templateSelected,
        currentBusinessInfo?.salon_name || "",
      );

      // Merge loaded preview data if available (after first step, use loaded data)
      const requestData =
        loadedPreviewData && Object.keys(loadedPreviewData).length > 0
          ? {
              ...blankRequest,
              ...loadedPreviewData,
              template_type: templateSelected, // Always use selected template type
              company_name:
                currentBusinessInfo?.salon_name ||
                loadedPreviewData.company_name ||
                "",
            }
          : blankRequest;

      const salonId = currentBusinessInfo?.id || "default-salon-id";
      const response = (await generateManualPreview(
        salonId,
        requestData,
      )) as any;

      if (response && response.website) {
        setTemplate({
          template_content: response.website,
          template_type: templateSelected,
          template_filename: "",
        });
      } else {
        setTemplateFetchError("Failed to generate template");
      }
    } catch (error) {
      setTemplateFetchError(
        error instanceof Error ? error.message : "Failed to generate template",
      );
    } finally {
      setIsTemplateLoading(false);
    }
  };

  const handleCloseModal = () => {
    // Close immediately without waiting for template fetch
    onClose();
  };

  // Handle Escape key to close modal immediately
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose(); // Call onClose directly to avoid dependency issues
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  const getStepTitle = (): string => {
    switch (currentStep) {
      case 1:
        return "Select a template";
      case 2:
        return "Choose your website address";
      default:
        return "";
    }
  };

  const getStepDescription = (): string => {
    switch (currentStep) {
      case 1:
        return "Pick a template to start designing your site. You can customize or change it later.";
      case 2:
        return "Pick a unique URL so customers can easily find you. We'll check if it's available.";
      default:
        return "";
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <TemplateSelectorWizard
            templateSelected={templateSelected}
            onTemplateSelect={setTemplateSelected}
          />
        );
      case 2:
        return (
          <WebsiteURLManagement
            businessInfo={salonBusinessInfo as BusinessInfo}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "white",
        display: "flex",
        flexDirection: "column",
        zIndex: 1000,
        overflow: "hidden",
      }}
    >
      <Paper
        p="xl"
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundColor: "transparent",
        }}
      >
        {/* Header */}
        <Group justify="space-between" mb="md">
          <Group>
            <Text fw={700} size="lg">
              Pagoda
            </Text>
          </Group>
          <Button variant="subtle" onClick={handleCloseModal}>
            <IconX size={16} />
          </Button>
        </Group>

        {/* Progress Bar */}
        <Progress
          value={(currentStep / totalSteps) * 100}
          size="sm"
          mb="xl"
          color="blue"
        />

        {/* Step Title and Description */}
        <Center mb="xl">
          <Stack align="center" gap="md">
            <Text
              size="2xl"
              fw={700}
              style={{ fontSize: "2.5rem", lineHeight: 1.2 }}
            >
              {getStepTitle()}
            </Text>
            <Text c="dimmed" size="lg" style={{ fontSize: "1.1rem" }}>
              {getStepDescription()}
            </Text>
          </Stack>
        </Center>

        {/* Step Content */}
        <Box
          style={{
            flex: 1,
            marginBottom: "20px",
            overflow: "auto",
            minHeight: 0,
          }}
        >
          {renderStepContent()}
        </Box>

        {/* Template Fetch Error */}
        {templateFetchError && (
          <Box mb="md">
            <Text c="red" size="sm" fw={500}>
              Error: {templateFetchError}
            </Text>
          </Box>
        )}

        {/* Navigation Buttons */}
        <Group justify="flex-end" gap="md">
          {currentStep > 1 && (
            <Button variant="outline" onClick={handleBack}>
              Back
            </Button>
          )}
          <Button variant="subtle" onClick={handleSkip}>
            Skip for Now
          </Button>
          <Button onClick={handleNext}>Next</Button>
        </Group>
      </Paper>
    </Box>
  );
};

export default WebsiteGeneratorWizardModal;
